/**
 * @file Constantes d'equilibrage globales (upgrades, stages, rebirth, etoiles, buffs, gacha, coffre, hors-ligne). Voir design/BALANCING.md.
 */
import type { BalanceConfig } from './types';

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

export const BALANCE: BalanceConfig = {
  upgrades: {
    specs: {
      // Power : x1.08 par niveau (original). Cout de base 50.
      power: { id: 'power', stat: 'damage', kind: 'mult', perLevel: 1.08, baseCost: 50 },
      // Speed : +8 (= +0.08 sur le multiplicateur) par niveau, additif (original).
      speed: { id: 'speed', stat: 'attackSpeed', kind: 'addMult', perLevel: 0.08, baseCost: 50 },
      // Money : x1.08 par niveau (original).
      money: { id: 'money', stat: 'money', kind: 'mult', perLevel: 1.08, baseCost: 50 },
    },
    costGrowth: 1.38,
    softCapLevel: 5000,
    costGrowthBeyondMult: 2,
    maxLevel: 10000,
    startLevel: 1,
    holdRepeatMs: { start: 350, min: 40, accel: 0.86 },
    // auto : etait 1 niveau / s (trop lent des que l'argent arrive vite) ; 4 passages / s, jusqu'a 25 niveaux chacun, le moins cher d'abord
    auto: { intervalSeconds: 0.25, maxLevelsPerTick: 25 },
  },
  stage: {
    levelsPerStage: 10,
    stagesPrecomputed: 5000,
    baseTreeHp: 250,
    hpGrowthBands: [
      { untilValue: 100, growth: 1.16 },
      { untilValue: 600, growth: 1.11 },
      { untilValue: MAX_SAFE, growth: 1.07 },
    ],
    moneyPerHp: 0.45,
    bossHpMult: 12,
    bossRewardMult: 12,
    // Foret dense : nombre d'arbres croissant DANS chaque stage (1-1 : 40 -> 1-10 : 76), base qui monte doucement de stage en
    // stage (+24 au plus), plafond 100 : la vie des arbres augmente aussi, le niveau ne doit pas devenir interminable.
    treeCount: { first: 40, levelGain: 36, stageGain: 24, stageScale: 6, max: 100 },
    // Chrono genereux (comme l'original) : 5 min par niveau, boss compris.
    levelSeconds: 300,
    bossLevelSeconds: 300,
    diaPerLevelClear: 3,
    diaPerBossClear: 25,
    biomeStageSpan: 1, // un theme par stage (1-x forest, 2-x desert, 3-x tundra, 4-x autumn, 5-x blossom, 6-x forest...)
  },
  combat: {
    baseCritChance: 0.05,
    baseCritDamage: 2,
    baseMoveSpeed: 6,
    approachRatio: 0.7,
    formationSpacing: 1.6,
    followRadius: 4.5,
    followReturnRatio: 0.55,
    overflowSpeedToDamage: true,
  },
  rebirth: {
    unlockStageValue: 30,
    bonusPerLevel: 1.05,
    maxLevel: 20000,
    rubyScale: 1,
    rubyExponent: 0.25,
    rubyMinMoneyAccum: 1e5,
    costBase: 3,
    costLinear: 0.5,
    costPower: 1.5,
    costPowerDiv: 4,
    dailyMultiplier: 3,
  },
  stars: {
    unlockStageValue: 390,
    maxLevel: 20,
    gemsPerLevel: 50,
    multBase: 1.5,
    multGrowth: 1.07,
    successBase: 1,
    successPerLevel: 0.045,
    successMin: 0.1,
    pityFromLevel: 18,
    pityStep: 0.15,
  },
  buffs: [
    { id: 'moneyBuff', stat: 'money', mult: 3, superMult: 4, secondsPerActivation: 300, maxSeconds: 900, cost: { dia: 30 }, freeEverySeconds: 7200 },
    { id: 'powerBuff', stat: 'damage', mult: 3, superMult: 4, secondsPerActivation: 300, maxSeconds: 900, cost: { dia: 30 }, freeEverySeconds: 7200 },
    { id: 'speedBuff', stat: 'attackSpeed', mult: 3, superMult: 4, secondsPerActivation: 300, maxSeconds: 900, cost: { dia: 30 }, freeEverySeconds: 7200 },
  ],
  gacha: {
    normal: {
      cost: { dia: 100 },
      table: [
        { grade: 'D', weight: 600 },
        { grade: 'C', weight: 300 },
        { grade: 'B', weight: 90 },
        { grade: 'A', weight: 10 },
      ],
      multiPullCount: 10,
      pity: { every: 50, minGrade: 'B' },
    },
    epic: {
      cost: { dia: 500 },
      table: [
        { grade: 'B', weight: 550 },
        { grade: 'A', weight: 350 },
        { grade: 'S', weight: 95 },
        { grade: 'SS', weight: 5 },
      ],
      multiPullCount: 10,
      pity: { every: 10, minGrade: 'A' },
    },
  },
  chest: {
    freeSlots: 6,
    paidSlots: 10,
    // Cout croissant de chaque emplacement payant (index 0..9) : 50, 75, ... 275 Dia.
    paidSlotCost: Array.from({ length: 10 }, (_, i) => ({ dia: 50 + 25 * i })),
  },
  // autoStepSeconds : la fusion auto se fait une paire a la fois (D+D -> C, puis C+C -> B...) pour que chaque palier se voie
  merge: { rule: 'sameGrade', autoMergeExcludeFromGradeIndex: 7, autoStepSeconds: 0.4 },
  equipSlotUnlocks: [0, 10, 30, 90],
  offline: {
    baseHoursCap: 8,
    efficiency: 0.5,
    minAwaySeconds: 60,
    travelOverheadSeconds: 1,
    avgCritFactorMode: 'expected',
  },
  tickets: {
    speedRun: { max: 3, regenSeconds: 4 * 3600, startCount: 3 },
    mine: { max: 5, regenSeconds: 2 * 3600, startCount: 3 },
    boss: { max: 3, regenSeconds: 6 * 3600, startCount: 3 },
    raid: { max: 2, regenSeconds: 12 * 3600, startCount: 2 },
    adventure: { max: 20, regenSeconds: 4 * 3600, startCount: 5 },
  },
  adventure: { coresPerHour: 20, xpPerHour: 10, maxLevel: 15 },
  startWallet: { money: 0, dia: 300 },
  // Skins : Argent x3.4 par skin (~1 skin tous les 8 niveaux de progression, l'argent d'un niveau x1.16) ; skins Gemmes
  // 600 -> 4550 (x1.5) = 2 a 18 jours de revenu regulier, au-dessus d'un tirage epique (500) et dans la fourchette des auras.
  // Bonus : paliers de 4 skins Argent (tier 1..5) puis +0.5 tier par skin Gemmes (5.5..8) : plus cher => jamais moins fort.
  skins: {
    moneyCount: 17,
    moneyBase: 2e4,
    moneyGrowth: 3.4,
    diaBase: 600,
    diaGrowth: 1.5,
    diaRound: 50,
    skinsPerTier: 4,
    diaTierStep: 0.5,
    bonusPerTier: { damage: 0.05, attackSpeed: 0.03, money: 0.05 },
  },
  // Familiers : seul debouche des Pieces de familier (mode Monstres 2 / vague, 60 max par partie de ~10 min, + 20 / jour
  // en mission). Poussin ~1 partie, Phenix ~70 parties : objectif long terme pour un bonus permanent de +30 % d'argent.
  pets: { petCoinByGrade: { D: 60, C: 150, B: 400, A: 900, S: 2000, SS: 4000 }, maxEquipped: 3 },
  // ~175 missions du jour + ~50 connexion + ~25 niveaux/boss (voir docs/ECONOMIE.md §Gemmes)
  economy: { diaPerDayCasual: 250, maxDaysForDiaItem: 40 },
};
